import React, { useEffect, useState, Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import MainPage from './pages/MainPage';
import { giftAPI, userAPI, setAuthToken } from './services/api';
import { useGameStore } from './store/gameStore';
import GiftModal from './components/GiftModal';
import { openExternalLink } from './ads/externalLink';
import { startAdsgramStartupLoop } from './ads/startupAds';
import { configureAdsRuntime } from './ads/adsRuntime';
import AdDebugPanel from './components/AdDebugPanel';
import { getSystemGiftUrl, setSystemGiftUrl } from './services/systemGiftConfig';

// Ленивая загрузка всех страниц кроме главной для оптимизации начального бандла
const TasksPage = React.lazy(() => import('./pages/TasksPage'));
const RoulettePage = React.lazy(() => import('./pages/RoulettePage'));
const FriendsPage = React.lazy(() => import('./pages/FriendsPage'));
const WalletPage = React.lazy(() => import('./pages/WalletPage'));
const GiftSponsorsPage = React.lazy(() => import('./pages/GiftSponsorsPage'));

// Компонент загрузки для ленивых страниц
const PageLoadingFallback = () => (
  <div className="auth-loading">
    <div className="auth-animation-container">
      <div className="auth-star">⭐</div>
      <div className="auth-loading-dots">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  </div>
);

// Функция для парсинга initData from Telegram WebApp
const parseInitData = (initDataString: string) => {
  const result: Record<string, any> = {};
  const params = new URLSearchParams(initDataString);
  
  // Преобразуем URLSearchParams в обычный объект
  Array.from(params.entries()).forEach(([key, value]) => {
    if (key === 'user') {
      try {
        result[key] = JSON.parse(value);
      } catch (e) {
        result[key] = value;
      }
    } else {
      result[key] = value;
    }
  });
  
  // Дополнительно извлекаем параметр 'start' если он есть в URL
  // Поддерживаем новый формат ссылок: https://t.me/Tapp1StarsBot/tappyapp?start=CODE
  if (params.has('start')) {
    result.start = params.get('start');
  }
  
  return result;
};

// Компонент для обработки аутентификации
const AuthHandler: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftLinkId, setGiftLinkId] = useState<number | null>(null);
  const [giftTargetUrl, setGiftTargetUrl] = useState<string | null>(null);
  const [giftImageSrc, setGiftImageSrc] = useState<string | null>(null);
  const { fetchUserData } = useGameStore();
  const navigate = useNavigate();

  const openGiftUrl = (url: string) => {
    openExternalLink(url, 'gift_link');
  };

  useEffect(() => {
    const handleAuth = async () => {
      try {
        setIsLoading(true);
        
        // Всегда пытаемся получить актуальные данные из Telegram WebApp
        const webApp = window.Telegram?.WebApp;
        if (webApp) {
          
          // Получаем initData из WebApp
          let initDataString = webApp.initData;
          const urlParams = new URLSearchParams(window.location.search);
          const rawHash = window.location.hash.replace(/^#/, '');
          const hashQuery = rawHash.includes('?') ? rawHash.split('?').slice(1).join('?') : rawHash;
          const hashParams = new URLSearchParams(hashQuery);
          const tgWebAppData = urlParams.get('tgWebAppData') ?? hashParams.get('tgWebAppData');
          const decodedTgWebAppData = tgWebAppData ? decodeURIComponent(tgWebAppData) : null;

          if (!initDataString && decodedTgWebAppData) {
            initDataString = decodedTgWebAppData;
          }

          if (!initDataString) {
            console.error('No initData available from Telegram');
            
            // Если нет initData, проверяем, есть ли сохраненный ID как запасной вариант
            const savedTelegramId = localStorage.getItem('telegram_id');
            if (savedTelegramId) {
              setAuthToken(savedTelegramId);
              setSessionReady(true);
              await fetchUserData();
              setIsLoading(false);
              return;
            }
            
            setAuthError('Не удалось получить данные из Telegram');
            setIsLoading(false);
            return;
          }
          
          // Проверяем доступные WebApp методы и свойства
          
          // Парсим данные из строки initData
          const parsedData = parseInitData(initDataString);
          const hashStartParam = hashParams.get('tgWebAppStartParam');
          const urlStartParam = urlParams.get('tgWebAppStartParam');
          const urlStart = urlParams.get('start') ?? hashParams.get('start');
          const urlStartapp = urlParams.get('startapp') ?? hashParams.get('startapp');
          const tgWebAppDataParams = decodedTgWebAppData ? new URLSearchParams(decodedTgWebAppData) : null;
          const tgWebAppDataStartParam = tgWebAppDataParams?.get('start_param')
            ?? tgWebAppDataParams?.get('start')
            ?? tgWebAppDataParams?.get('startapp');
          const unsafeStartParam = webApp.initDataUnsafe?.start_param as string | undefined;
          const unsafeStart = (webApp.initDataUnsafe as { start?: string } | undefined)?.start;
          const unsafeStartapp = (webApp.initDataUnsafe as { startapp?: string } | undefined)?.startapp;

          if (!parsedData.start_param && unsafeStartParam) {
            parsedData.start_param = unsafeStartParam;
          }
          if (!parsedData.start_param && tgWebAppDataStartParam) {
            parsedData.start_param = tgWebAppDataStartParam;
          }
          if (!parsedData.start_param && (urlStartParam || hashStartParam)) {
            parsedData.start_param = urlStartParam || hashStartParam;
          }
          if (!parsedData.start && unsafeStart) {
            parsedData.start = unsafeStart;
          }
          if (!parsedData.startapp && unsafeStartapp) {
            parsedData.startapp = unsafeStartapp;
          }
          if (!parsedData.start && urlStart) {
            parsedData.start = urlStart;
          }
          if (!parsedData.startapp && urlStartapp) {
            parsedData.startapp = urlStartapp;
          }

          const derivedStartParam = parsedData.start_param
            ?? tgWebAppDataStartParam
            ?? urlStartParam
            ?? hashStartParam
            ?? parsedData.start
            ?? parsedData.startapp
            ?? unsafeStartParam
            ?? unsafeStart
            ?? unsafeStartapp;
          const clientUrlDebug = {
            search: window.location.search,
            hash: window.location.hash,
            href: window.location.href
          };

          console.log('[FRONTEND DEBUG] Extracted start params:', {
            start_param: parsedData.start_param,
            start: parsedData.start,
            startapp: parsedData.startapp,
            urlStart,
            urlStartapp,
            urlStartParam,
            hashStartParam,
            tgWebAppDataStartParam,
            unsafeStartParam,
            unsafeStart,
            unsafeStartapp,
            rawHash,
            hashQuery,
            search: clientUrlDebug.search,
            hash: clientUrlDebug.hash,
            href: clientUrlDebug.href
          });
          
          // Извлекаем информацию о пользователе
          let userData = parsedData.user ? 
            (typeof parsedData.user === 'string' ? JSON.parse(parsedData.user) : parsedData.user) : {};
          
          
          // Проверяем наличие обязательных полей
          if (!userData.id) {
            console.error('Missing required field: id in user data');
            
            // Альтернативный вариант - получить данные из initDataUnsafe
            if (webApp.initDataUnsafe && webApp.initDataUnsafe.user) {
              userData = webApp.initDataUnsafe.user;
            } else {
              setAuthError('Не удалось получить ID пользователя Telegram');
              setIsLoading(false);
              return;
            }
          }
          
          // Формируем полный объект авторизации с гарантированными полями
          const authData = {
            user: userData,
            hash: parsedData.hash || webApp.initDataUnsafe?.hash,
            auth_date: parsedData.auth_date || webApp.initDataUnsafe?.auth_date,
            // Добавляем другие возможные поля из initData
            ...(parsedData.query_id && { query_id: parsedData.query_id }),
            ...(parsedData.chat_instance && { chat_instance: parsedData.chat_instance }),
            ...(parsedData.chat_type && { chat_type: parsedData.chat_type }),
            ...(derivedStartParam && { start_param: derivedStartParam }),
            ...(parsedData.start && { start: parsedData.start }),
            ...(parsedData.startapp && { startapp: parsedData.startapp }),
            clientUrl: clientUrlDebug,
            // Для отладки - добавляем raw данные
            initDataRaw: initDataString
          };

          console.log('[FRONTEND DEBUG] Auth payload start fields:', {
            start_param: authData.start_param,
            start: authData.start,
            startapp: authData.startapp,
            hasInitDataRaw: !!authData.initDataRaw,
            clientUrl: authData.clientUrl
          });
          
          
          try {
            // Отправляем данные на сервер для аутентификации
            const authResponse = await userAPI.login(authData);
            
            // Получаем данные пользователя из ответа
            if (authResponse && authResponse.user) {
              // Сохраняем Telegram ID для последующих визитов
              const telegramId = authResponse.user.telegramId;
              setAuthToken(telegramId);
              localStorage.setItem('telegram_id', telegramId);
              // Загружаем данные пользователя после авторизации
              await fetchUserData();
              setAuthReady(true);
              setSessionReady(true);

              // По умолчанию после авторизации отправляем пользователя на главный экран "Тапать".
              // Если в start_param явно передан переход в tasks, ниже логика сделает то же самое.
              let redirectedToTasks = false;
              try {
                const startParam = (parsedData.start_param || parsedData.start || (parsedData as any).startapp || webApp.initDataUnsafe?.start_param) as string | undefined;
                if (typeof startParam === 'string') {
                  const sp = startParam.toLowerCase();
                  if (sp === 'tasks' || sp === 'task' || sp.startsWith('tasks:') || sp.includes('action=tasks')) {
                    navigate('/tasks');
                    redirectedToTasks = true;
                  }
                }
              } catch (e) {
              }

              if (!redirectedToTasks) {
                navigate('/');
              }
            } else {
              console.error('Auth failed - no user data received');
              setAuthError('Не удалось получить данные пользователя');
            }
          } catch (error: any) {
            console.error('Auth request failed:', error);
            
            if (error.response) {
              console.error('Server response error:', {
                status: error.response.status,
                data: error.response.data
              });
              
              if (error.response.data && error.response.data.errors) {
                const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
                setAuthError(`Ошибка аутентификации: ${errorMessages}`);
              } else if (error.response.data && error.response.data.error) {
                setAuthError(`Ошибка аутентификации: ${error.response.data.error}`);
              } else {
                setAuthError(`Ошибка аутентификации: ${error.response.status}`);
              }
            } else {
              setAuthError('Не удалось подключиться к серверу аутентификации');
            }
            
            throw error;
          }
        } else {
          setAuthError('Приложение должно запускаться в Telegram');
        }
      } catch (error) {
        console.error('Authentication error:', error);
        setAuthError('Ошибка аутентификации');
      } finally {
        setIsLoading(false);
      }
    };
    
    handleAuth();
  }, []);

  // UI загружен — через 5 секунд стартует фоновая цепочка Adsgram (rewarded + interstitial)
  // и дальше повторяется по кулдауну, как в postingBot.
  useEffect(() => {
    if (!sessionReady) return;
    configureAdsRuntime();
    return startAdsgramStartupLoop();
  }, [sessionReady]);

  useEffect(() => {
    if (!authReady) return;

    giftAPI.getChainConfig().then((config) => setSystemGiftUrl(config.systemGiftUrl)).catch(() => {});

    const giftImages = (() => {
      try {
        const ctx = (require as any).context('./assets/gifts', false, /\.(png|jpe?g|gif|svg|webp)$/i);
        return ctx.keys().map((k: string) => {
          const result = ctx(k);
          return result?.default || result;
        });
      } catch {
        return [] as string[];
      }
    })();

    const getRandomGiftImage = () => (
      giftImages.length ? giftImages[Math.floor(Math.random() * giftImages.length)] : null
    );

    const getRandomExclusiveLink = () => {
      const links = ['https://t.me/your_stars_presents_bot?startapp', getSystemGiftUrl()];
      return links[Math.floor(Math.random() * links.length)];
    };

    const loadSponsorsPage = async (): Promise<boolean> => {
      try {
        const resp = await giftAPI.getSponsors();
        if (resp?.success && Array.isArray(resp.tasks) && resp.tasks.length > 0) {
          navigate('/gift-sponsors', { state: { prefetchedTasks: resp.tasks } });
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

    const loadHardcodedFallback = () => {
      const link = getRandomExclusiveLink();
      if (!link) return false;

      setGiftLinkId(null);
      setGiftTargetUrl(link);
      setGiftImageSrc(getRandomGiftImage());
      setGiftOpen(true);
      return true;
    };

    const timer = setTimeout(async () => {
      try {
        const priority = await giftAPI.getRussiaPriority();
        const priorityUrl = priority?.link?.trackingUrl || priority?.link?.url;
        if (priority?.success && priority?.available && priorityUrl) {
          setGiftLinkId(priority.link?.trackingUrl ? null : priority.link?.id ?? null);
          setGiftTargetUrl(priorityUrl);
          setGiftImageSrc(getRandomGiftImage());
          setGiftOpen(true);
          return;
        }

        const resp = await giftAPI.getAvailable();
        const directUrl = resp?.link?.trackingUrl || resp?.link?.url;
        if (!resp || !resp.success || !resp.available || !directUrl) {
          const sponsorsLoaded = await loadSponsorsPage();
          if (sponsorsLoaded) return;

          if (!priority?.eligible) loadHardcodedFallback();
          return;
        }

        const image = getRandomGiftImage();

        setGiftLinkId(resp.link?.id ?? null);
        setGiftTargetUrl(directUrl);
        setGiftImageSrc(image);
        setGiftOpen(true);
      } catch (e) {
      }
    }, 60_000);

    return () => clearTimeout(timer);
  }, [authReady]);

  const normalizeGiftLink = (rawLink: string): string => {
    if (!rawLink) return '';
    let url = rawLink.trim();

    if (!url.startsWith('http://') && !url.startsWith('https://') && url.startsWith('t.me/')) {
      url = `https://${url}`;
    }

    if (url.startsWith('tg://resolve?')) {
      try {
        const parsed = new URL(url);
        const domain = parsed.searchParams.get('domain');
        if (domain) {
          url = `https://t.me/${domain}`;
        }
      } catch {
      }
    }

    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('tg://')) {
      if (url.includes('.')) {
        url = `https://${url}`;
      }
    }

    return url;
  };

  const openGift = async () => {
    if (!giftTargetUrl) {
      setGiftOpen(false);
      return;
    }

    const url = normalizeGiftLink(giftTargetUrl);
    if (!url) {
      setGiftOpen(false);
      return;
    }

    if (giftLinkId) {
      await giftAPI.trackClick(giftLinkId).catch(() => {});
    }
    openGiftUrl(url);

    setGiftOpen(false);
  };
  
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="auth-animation-container">
          <div className="auth-star">⭐</div>
          <div className="auth-text">TappyStars</div>
          <div className="auth-loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }
  
  if (authError) {
    return (
      <div className="auth-error">
        <p>{authError}</p>
        <button onClick={() => window.location.reload()}>Попробовать снова</button>
      </div>
    );
  }
  
  return (
    <>
      {children}
      <GiftModal
        isOpen={giftOpen}
        imageSrc={giftImageSrc}
        onClose={() => setGiftOpen(false)}
        onClaim={openGift}
      />
      <AdDebugPanel />
    </>
  );
};

// Добавляем немного стилей для индикатора загрузки
const AppWithStyles: React.FC = () => {
  return (
    <>
      <style>
        {`
          .auth-loading, .auth-error {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            font-family: sans-serif;
            background: linear-gradient(135deg, #161d29 0%, #1c2430 100%);
          }
          .auth-error {
            color: #ff4d4f;
          }
          .auth-error button {
            margin-top: 16px;
            padding: 8px 16px;
            background-color: #1890ff;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
          }
          
          .auth-animation-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
          }
          
          .auth-star {
            font-size: 80px;
            margin-bottom: 20px;
            animation: auth-star-float 3s ease-in-out infinite;
            filter: drop-shadow(0 0 20px rgba(255, 215, 0, 0.6));
            color: #FFD700;
          }
          
          .auth-text {
            font-size: 36px;
            font-weight: bold;
            background: linear-gradient(45deg, #FFD700, #FFA500, #FFD700);
            background-size: 200% 200%;
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 30px;
            animation: auth-text-shine 2s ease-in-out infinite, auth-text-glow 3s ease-in-out infinite;
            letter-spacing: 2px;
            text-shadow: 0 0 30px rgba(255, 215, 0, 0.3);
          }
          
          .auth-loading-dots {
            display: flex;
            gap: 8px;
            justify-content: center;
          }
          
          .auth-loading-dots span {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: linear-gradient(45deg, #FFD700, #FFA500);
            animation: auth-dots-bounce 1.4s ease-in-out infinite both;
            box-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
          }
          
          .auth-loading-dots span:nth-child(1) {
            animation-delay: -0.32s;
          }
          
          .auth-loading-dots span:nth-child(2) {
            animation-delay: -0.16s;
          }
          
          @keyframes auth-star-float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-15px) rotate(5deg);
            }
          }
          
          @keyframes auth-text-shine {
            0% {
              background-position: -200% center;
            }
            100% {
              background-position: 200% center;
            }
          }
          
          @keyframes auth-text-glow {
            0%, 100% {
              filter: drop-shadow(0 0 20px rgba(255, 215, 0, 0.3));
            }
            50% {
              filter: drop-shadow(0 0 30px rgba(255, 215, 0, 0.6));
            }
          }
          
          @keyframes auth-dots-bounce {
            0%, 80%, 100% {
              transform: scale(0.8);
              opacity: 0.5;
            }
            40% {
              transform: scale(1.2);
              opacity: 1;
            }
          }
        `}
      </style>
      
    <HashRouter>
        <AuthHandler>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tap" element={<MainPage />} />
          <Route path="/roulette" element={<RoulettePage />} />
          <Route path="/friends" element={<FriendsPage />} />
          <Route path="/wallet" element={<WalletPage />} />
          <Route path="/gift-sponsors" element={<GiftSponsorsPage />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
        </AuthHandler>
    </HashRouter>
    </>
  );
};

export default AppWithStyles;
